import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { X, Plus, Edit, Loader2, Search, ChevronDown } from 'lucide-react';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { getFieldOptionsSync, isSelectField } from '../../hooks/useFieldOptions';

const BulkFieldUpdateModal = ({ 
  isOpen, 
  onClose, 
  entityName = 'customer', 
  entityIds = [], 
  entityCount = 0,
  customerType = null, // 'Person' or 'Company' for customer entity
  onSuccess 
}) => {
  const { t, i18n } = useTranslation();
  const { config: entityConfig } = useEntityConfig(entityName);
  const [fieldUpdates, setFieldUpdates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lookupData, setLookupData] = useState({}); // Cache for lookup field data
  const [lookupSearchTerms, setLookupSearchTerms] = useState({}); // Search terms for lookup fields
  const [lookupDropdownOpen, setLookupDropdownOpen] = useState({}); // Track which lookup dropdowns are open

  // Get editable fields based on customer type
  const editableFields = useMemo(() => {
    if (!entityConfig || !entityConfig.fields) return [];
    
    const fields = [];
    const isThaiLanguage = i18n?.language?.toLowerCase().startsWith('th') || false;
    
    Object.entries(entityConfig.fields).forEach(([fieldName, fieldConfig]) => {
      // Skip hidden fields and auto-generated IDs
      if (fieldConfig.import?.enabled === false || 
          fieldConfig.validation?.autoGenerated) {
        return;
      }
      
      // Skip ID fields, but allow:
      // - Primary key
      // - Fields with lookup enabled (like SalespersonID)
      // - Fields in Customers table (like SalespersonID)
      if (fieldName.endsWith('ID') && fieldName !== entityConfig.entity?.primaryKey) {
        const dbTable = fieldConfig.database?.table;
        const hasLookup = fieldConfig.lookup?.enabled;
        // Allow if it's in Customers table or has lookup enabled
        if (dbTable !== 'Customers' && !hasLookup) {
          return;
        }
      }
      
      // Filter by customer type if specified (for customer entity)
      if (customerType && entityName === 'customer') {
        const visibleWhen = fieldConfig.ui?.visibleWhen;
        if (visibleWhen) {
          // Check if field should be visible for this customer type
          if (visibleWhen.includes(`CustomerType = 'Person'`) && customerType !== 'Person') {
            return;
          }
          if (visibleWhen.includes(`CustomerType = 'Company'`) && customerType !== 'Company') {
            return;
          }
        }
        
        // Also check database table - Person fields for Person tab, Company fields for Company tab
        const dbTable = fieldConfig.database?.table;
        if (customerType === 'Person' && dbTable === 'Companies' && fieldName !== 'PersonCompanyID') {
          return; // Skip company-only fields on person tab
        }
        if (customerType === 'Company' && dbTable === 'Persons') {
          return; // Skip person-only fields on company tab
        }
      }
      
      // Check if field is editable (same logic as getEditableField in Customers.js)
      const dbTable = fieldConfig.database?.table;
      let isEditable = false;
      
      if (customerType === 'Person' && dbTable === 'Persons') {
        isEditable = true;
      } else if (customerType === 'Company' && dbTable === 'Companies') {
        isEditable = true;
      } else if (dbTable === 'Customers') {
        isEditable = true; // Always editable (base table)
      } else if (fieldConfig.storage?.type === 'attributes') {
        isEditable = true; // Custom attributes are always editable
      }
      
      if (!isEditable) return;
      
      const displayName = isThaiLanguage && fieldConfig.displayNameTH 
        ? fieldConfig.displayNameTH 
        : (fieldConfig.displayName || fieldName);
      
      fields.push({
        value: fieldName,
        label: displayName,
        fieldName,
        dataType: fieldConfig.dataType || 'string',
        fieldConfig
      });
    });
    
    // Sort by label
    return fields.sort((a, b) => a.label.localeCompare(b.label));
  }, [entityConfig, i18n?.language, customerType, entityName]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFieldUpdates([]);
      setLookupData({});
      setLookupSearchTerms({});
      setLookupDropdownOpen({});
    }
  }, [isOpen]);

  // Fetch lookup data when a lookup field is selected
  const fetchLookupData = async (fieldName, lookupConfig) => {
    if (lookupData[fieldName]) {
      return; // Already fetched
    }

    try {
      let data = [];
      
      if (fieldName === 'SalespersonID' && lookupConfig.table === 'Users') {
        // Fetch salespersons
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          // Filter to only include users with Salesperson role
          const salespersonOnly = (result.salespersons || []).filter(user => {
            const roles = user.Roles || '';
            return roles.includes('Salesperson');
          });
          data = salespersonOnly.map(sp => ({
            id: sp.UserID,
            display: `${sp.FirstName || ''} ${sp.LastName || ''}`.trim() || sp.Username || sp.Email,
            searchText: `${sp.FirstName || ''} ${sp.LastName || ''} ${sp.Username || ''} ${sp.Email || ''}`.toLowerCase(),
            ...sp
          }));
        }
      }
      // Add more lookup types here as needed
      
      setLookupData(prev => ({
        ...prev,
        [fieldName]: data
      }));
    } catch (error) {
      console.error(`Failed to fetch lookup data for ${fieldName}:`, error);
      setLookupData(prev => ({
        ...prev,
        [fieldName]: []
      }));
    }
  };

  // Add new field update
  const handleAddField = () => {
    const newUpdate = {
      id: Date.now().toString(),
      field: editableFields[0]?.value || '',
      value: ''
    };
    setFieldUpdates([...fieldUpdates, newUpdate]);
  };

  // Remove field update
  const handleRemoveField = (updateId) => {
    setFieldUpdates(fieldUpdates.filter(u => u.id !== updateId));
  };

  // Update field update
  const handleUpdateChange = (updateId, key, value) => {
    setFieldUpdates(fieldUpdates.map(update => {
      if (update.id === updateId) {
        const newUpdate = { ...update, [key]: value };
        // If field changed, reset value and fetch lookup data if needed
        if (key === 'field') {
          newUpdate.value = '';
          const field = getSelectedField(value);
          if (field?.fieldConfig?.lookup?.enabled) {
            fetchLookupData(value, field.fieldConfig.lookup);
          }
        }
        return newUpdate;
      }
      return update;
    }));
  };

  // Get selected field object
  const getSelectedField = (fieldValue) => {
    return editableFields.find(f => f.value === fieldValue);
  };

  // Fetch lookup data when field updates change
  useEffect(() => {
    fieldUpdates.forEach(update => {
      if (update.field) {
        const field = getSelectedField(update.field);
        if (field?.fieldConfig?.lookup?.enabled && !lookupData[field.fieldName]) {
          fetchLookupData(field.fieldName, field.fieldConfig.lookup);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldUpdates.map(u => `${u.id}-${u.field}`).join(',')]);

  // Render value input based on field type
  const renderValueInput = (update) => {
    const field = getSelectedField(update.field);
    if (!field) {
      return (
        <div className="flex-1 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
          {t('filter.selectFieldFirst', 'Please select a field first')}
        </div>
      );
    }

    // Lookup field (e.g., SalespersonID) - Check this FIRST before data type checks
    if (field.fieldConfig?.lookup?.enabled) {
      const lookupKey = update.id;
      const searchTerm = lookupSearchTerms[lookupKey] || '';
      const isOpen = lookupDropdownOpen[lookupKey] || false;
      const data = lookupData[field.fieldName] || [];
      
      // Filter data based on search term
      const filteredData = data.filter(item => 
        item.searchText.includes(searchTerm.toLowerCase())
      );
      
      // Find selected item
      const selectedItem = data.find(item => String(item.id) === String(update.value));
      
      return (
        <div className="flex-1 relative">
          <div className="relative">
            <input
              type="text"
              value={selectedItem ? selectedItem.display : searchTerm}
              onChange={(e) => {
                setLookupSearchTerms(prev => ({ ...prev, [lookupKey]: e.target.value }));
                setLookupDropdownOpen(prev => ({ ...prev, [lookupKey]: true }));
                if (!selectedItem) {
                  handleUpdateChange(update.id, 'value', '');
                }
              }}
              onFocus={() => setLookupDropdownOpen(prev => ({ ...prev, [lookupKey]: true }))}
              placeholder={t('filter.enterValue', 'Search by name...')}
              className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
          </div>
          
          {isOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setLookupDropdownOpen(prev => ({ ...prev, [lookupKey]: false }))}
              />
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredData.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                    {data.length === 0 ? t('filter.loadingValues', 'Loading...') : t('filter.noValuesFound', 'No matches found')}
                  </div>
                ) : (
                  filteredData.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        handleUpdateChange(update.id, 'value', item.id);
                        setLookupSearchTerms(prev => ({ ...prev, [lookupKey]: '' }));
                        setLookupDropdownOpen(prev => ({ ...prev, [lookupKey]: false }));
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                      {item.display}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    // Date/datetime input
    if (field.dataType === 'date' || field.dataType === 'datetime') {
      const inputType = field.dataType === 'date' ? 'date' : 'datetime-local';
      return (
        <input
          type={inputType}
          value={update.value || ''}
          onChange={(e) => handleUpdateChange(update.id, 'value', e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      );
    }

    // Boolean input
    if (field.dataType === 'boolean') {
      return (
        <select
          value={update.value || ''}
          onChange={(e) => handleUpdateChange(update.id, 'value', e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{t('filter.selectValue', 'Select value...')}</option>
          <option value="true">{t('common.yes', 'Yes')}</option>
          <option value="false">{t('common.no', 'No')}</option>
        </select>
      );
    }

    // Number input (only if not a lookup field)
    if (['integer', 'number', 'decimal'].includes(field.dataType)) {
      return (
        <input
          type="number"
          value={update.value || ''}
          onChange={(e) => handleUpdateChange(update.id, 'value', e.target.value)}
          step={field.dataType === 'decimal' ? '0.01' : '1'}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      );
    }

    // Select/choice field
    if (isSelectField(entityConfig, field.fieldName)) {
      const fieldOptions = getFieldOptionsSync(entityConfig, field.fieldName);
      return (
        <select
          value={update.value || ''}
          onChange={(e) => handleUpdateChange(update.id, 'value', e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{t('filter.selectValue', 'Select value...')}</option>
          {fieldOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label || option.value}
            </option>
          ))}
        </select>
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        value={update.value || ''}
        onChange={(e) => handleUpdateChange(update.id, 'value', e.target.value)}
        placeholder={t('filter.enterValue', 'Enter value...')}
        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
      />
    );
  };

  // Validate updates
  const validateUpdates = () => {
    if (fieldUpdates.length === 0) {
      toast.error('Please add at least one field to update');
      return false;
    }

    for (const update of fieldUpdates) {
      if (!update.field) {
        toast.error('Please select a field for all updates');
        return false;
      }
      if (update.value === '' || update.value === undefined || update.value === null) {
        const field = getSelectedField(update.field);
        if (field && field.fieldConfig?.validation?.required) {
          toast.error(`Field "${field.label}" is required`);
          return false;
        }
      }
    }

    return true;
  };

  // Apply updates
  const handleApply = async () => {
    if (!validateUpdates()) {
      return;
    }

    try {
      setSaving(true);

      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch('/api/customers/bulk-update-fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify({
          customerIds: entityIds,
          updates: fieldUpdates.map(u => ({
            fieldName: u.field,
            fieldValue: u.value
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to update fields');
      }

      const result = await response.json();
      
      const successCount = result.results?.successfulUpdates || 0;
      const failedCount = result.results?.failedUpdates || 0;
      
      if (failedCount === 0) {
        toast.success(
          `Successfully updated ${successCount} field${successCount !== 1 ? 's' : ''} for ${result.results?.updatedCustomers || entityCount} customer${result.results?.updatedCustomers !== 1 ? 's' : ''}`
        );
      } else {
        toast.success(
          `Updated ${successCount} field${successCount !== 1 ? 's' : ''} successfully. ${failedCount} update${failedCount !== 1 ? 's' : ''} failed.`
        );
      }

      // Reset state
      setFieldUpdates([]);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to update fields:', error);
      toast.error(error.message || 'Failed to update fields');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setFieldUpdates([]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('customers.updateFields', 'Update Field Values')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Update fields for {entityCount} selected {entityName.toLowerCase()}{entityCount !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!entityConfig ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header with Add Field button */}
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-900 dark:text-white">
                  {t('customers.fieldsToUpdate', 'Fields to Update')}
                </label>
                <button
                  type="button"
                  onClick={handleAddField}
                  className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30"
                >
                  <Plus className="h-3 w-3" />
                  <span>{t('filter.addFilter', 'Add Field')}</span>
                </button>
              </div>

              {/* Field Update Rules */}
              {fieldUpdates.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                  {t('customers.noFieldsToUpdate', 'No fields to update. Click "Add Field" to start.')}
                </div>
              ) : (
                <div className="space-y-3">
                  {fieldUpdates.map((update) => {
                    const field = getSelectedField(update.field);
                    return (
                      <div
                        key={update.id}
                        className="flex flex-col sm:flex-row gap-2 p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        {/* Field Selector */}
                        <select
                          value={update.field || ''}
                          onChange={(e) => handleUpdateChange(update.id, 'field', e.target.value)}
                          className="flex-1 sm:flex-none sm:w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">{t('filter.selectField', 'Select field...')}</option>
                          {editableFields.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </select>

                        {/* Value Input */}
                        {field && (
                          <div className="flex-1">
                            {renderValueInput(update)}
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveField(update.id)}
                          className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title={t('filter.removeFilter', 'Remove field')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleApply}
            disabled={saving || fieldUpdates.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{t('customers.applyUpdates', 'Apply Updates')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkFieldUpdateModal;

